/*
 * Get ready — `getready.list` domain model (App-shell canon §4, FLEXIBLE).
 *
 * SHIPPED structure contract + derivations for the per-event readiness cards.
 * Transcribed from the canon §2.4 (global state model) + §4.2 (card states,
 * derived NEVER stored) + §4.3 (step-row state machine). The canon demo
 * dataset lives in src/data/seed.ts as a TEST FIXTURE ONLY (Ruling A,
 * BOARDROOM-2026-08-09-133534 / PROGRAMME-AD-NO-FABRICATED-DATA-IN-BUILDS-V1):
 * nothing in this module carries demo values.
 *
 * READINESS AUTHORITY (Ruling C, BOARDROOM-2026-08-09-133534): the SERVER is
 * the readiness authority — no client-persisted completion boolean exists, and
 * the UI CONSUMES the server's readiness result (every card state / pill / pct /
 * badge below is a pure derivation over the server-provided step rows). NOTE:
 * profile completion is RECORDED to the server on save via `onProfileSaved`
 * below (a server write, not a client flag) — see its deviation note: Ruling
 * C's intended server-side DERIVATION was never implemented, so until a
 * participant-profile store lands the client must tell the server the profile
 * is done. This remains server-authoritative for READS.
 */
import {
  CHILD_DETAILS_REQUIRED, fetchAllReadiness, fetchMyHoldings, fetchReadiness, markProfileComplete, myApiBase,
  MyApiError, type QuestionnaireStatus, type Readiness,
} from './myApi'
import { loadSession } from '../auth/session'
import { eventQuestionnaireQuestions, questionnaireComplete } from './participantConfig'
import { isPastEvent } from './pastEvents'
import { translate, type TranslateFn } from '../i18n/useLocale'
import {
  eligibilityGate, eligibilityMismatchCopy, eligibilityRejectCopy, type TicketEligibility,
} from './eligibility'
import type { Ticket } from './types'

/** Default resolver — English, so the pure derivations stay directly
 *  assertable in unit tests; screens pass the live locale's t (the wizardModel
 *  / waiverModel pattern). P5 review follow-up: the step label/meta strings
 *  below were the last hardcoded-English screen model (P1 gap) — they now
 *  resolve through the `getReady.*` i18n keys. */
const enT: TranslateFn = (k, p) => translate('en', k, p)

// ---------------------------------------------------------------------------
// Structure contract (canon §2.4 / §4.3)
// ---------------------------------------------------------------------------

/** Step-row states. `blocked` (BOARDROOM-2026-08-29-120259 ruling 3 +
 *  ADDENDUM 2 123126): the step's DATA is complete but the participant is not
 *  eligible for THIS ticket (gender or age axis) — "complete — not valid for
 *  this ticket", NEVER reported as "Details missing". It is a per-ticket
 *  ELIGIBILITY OVERLAY on top of the canon {now,done,soon} machine, not a
 *  completion state: it is derived per render from server truth + the saved
 *  profile (applyProfileMismatch), never stored, and never counts as a `now`
 *  to-do (no re-nag once the participant has done all they can). */
export type GetReadyStepState = 'done' | 'now' | 'soon' | 'blocked'
/** `childConfirm` (guardian-claim S2 slice, BOARDROOM-2026-09-03-052111 /
 *  CMT-2026-09-03-020): the guardian-claimed seat's child-identity step —
 *  child name confirmed, DOB + gender collected (the PARENT is the authority;
 *  the no-permission path captures neither at assign time). Rendered ONLY on
 *  guardian-claimed seats via applyGuardianClaim. */
export type GetReadyStepKey =
  | 'profile' | 'waiver' | 'emergency' | 'questionnaire' | 'heat' | 'ticket' | 'childConfirm'
/** The only user CTA labels the canon step machine emits (§4.3).
 *  `Choose` is the OnePulse-canon CTA for the required heat-selection step. */
export type GetReadyStepCta = 'Start' | 'Resume' | 'Sign' | 'Add' | 'Edit' | 'View' | 'Choose'

/** One step row (canon §4.3 state machine: state ∈ {now, done, soon} × system). */
export interface GetReadyStep {
  /** Stable step key for DOM ids/testids (`getready-step-{key}`). Step sets
   *  are PER-EVENT, never a hardcoded six rows (canon §4.3 must-nots). */
  key: GetReadyStepKey
  label: string
  meta: string
  state: GetReadyStepState
  /** "we do this" — organiser/system step. NEVER gets a user CTA (§4.3). */
  system?: boolean
  /** Present ⇔ the step has a wired flow. `now` → accent CTA; `done` →
   *  outline Edit/View; absent on a done step → no right column (§4.3). */
  cta?: GetReadyStepCta
  /** Managed-child subject key for guardian-variant steps.
   *  TODO(V1.1 kids amendment §12.2): child-subject cards are deferred. */
  subject?: string
  /** Profile wizard step a Start lands on, when not step 1 — the fitness-only
   *  profile block opens the preferences step that carries the fitness
   *  question (boardroom 2026-10-01). Absent ⇒ the normal entry. */
  entryStep?: number
  /** Overrides the mono tag on a non-done row without a CTA (teen self-holder
   *  "PARENT SIGNS"). Absent ⇒ the standard SCHEDULED / WE DO THIS tags. */
  tag?: string
}

/** The profile wizard step that carries the fitness_self question (the
 *  preferences step — ProfileWizard MAX_STEP; it renders whenever the event
 *  asks fitness). */
export const FITNESS_WIZARD_STEP = 5

export interface GetReadyOrganiser {
  name: string
  /** `{org.fg}` — card left border, kicker, step connector (canon §4.2).
   *  Production values come from the central Trumin asset store per org-unit
   *  (canon §2.1); absent an org record the app accent is used. */
  accentFg: string
}

/** One Get-ready card: ONE HOLDING — a place the signed-in account holds in
 *  an event, for themselves OR for a child they sign for (canon §4 purpose +
 *  the child-holder launch-gate slice). PER-HOLDING, not per-event: a
 *  guardian holding their own seat AND a child seat in the SAME event gets
 *  TWO cards (the old byEvent collapse hid one of them). */
export interface GetReadyEvent {
  /** Stable data key (`data-event-id`) — the EVENT id. NOT unique across
   *  cards since the per-holding change; `id|ticketId` is the card key. */
  id: string
  /** The held ticket BACKING this card (child-holder slice) — every step CTA
   *  routes with THIS ticket, never a per-event lookup that could cross two
   *  same-event holdings. Optional: fixture-driven renders omit it and the
   *  legacy ticketByEvent map then backs the card (today's behaviour). */
  ticketId?: string
  /** Holder kind off the /me/holdings wire (child-holder slice). ABSENT on an
   *  older my-api ⇒ exactly the pre-slice self-subject rendering. */
  holderKind?: import('./myApi').HolderKind
  /** The server's child-context mark off /me/holdings (holdingChildContext —
   *  kind 'child' OR claim_kind 'guardian'): the card's questionnaire status
   *  read and its questionnaire entry key on the child via
   *  questionnaireSubjectTicket. Absent = no server mark. */
  childContext?: boolean
  /** The card SUBJECT's full name — drives the "For {name}" identity line on
   *  EVERY card (Duncan UI order BOARDROOM-135429: "what am I signing in here
   *  for? Which ticket is it?"). child holdings → the wire's holderName;
   *  every other holding is the CALLER's own seat → the signed-in holder's
   *  name from the verified session claims (nameFromClaims — the waiver
   *  prefill source). null/absent ⇒ the line is omitted honestly (never an
   *  invented name). */
  holderName?: string | null
  /** Managed-child profile key when the card's subject is a LOCALLY-RECORDED
   *  child (assign-store overlay, Duncan rulings BOARDROOM-2026-09-02-150714
   *  items 2–3): Edit-follows-the-holder routes the profile/emergency steps
   *  to /profile/children/{childKey} — NEVER the account owner's wizard.
   *  null/absent = no local child record known for this ticket. */
  childKey?: string | null
  /** PARENT-SIGNING visibility card (ruling item 5, corrected model
   *  BOARDROOM-2026-09-02-152856): the buyer's view of a seat DEFERRED to
   *  the child's own parent (server holder = invitee at the parent's email).
   *  Present ⇒ the card renders the read-only state — visible, NEVER
   *  actionable (no CTAs, no steps). State is the wire's honest carry:
   *  holder.state invited/claimed, 'done' when the server exposes it
   *  (holderComplete marker, or the ready/consumed holder states).
   *  `qrAvailable` is the SERVER's purchaser-QR marker — strictly wire-read
   *  (absent today = false): only true admits the seat to the buyer's wallet
   *  (no signature → no QR anywhere). */
  waiting?: {
    parentName: string
    parentEmail: string
    state: 'invited' | 'claimed' | 'done'
    qrAvailable?: boolean
    /** The owning order — the purchaser-scoped scan-token route (PR#125,
     *  GET /me/orders/{orderId}/tickets/{ticketId}/scan-token) is order-
     *  addressed; the holder-scoped route 404s for the buyer. */
    orderId?: string
  }
  /** GUARDIAN-CLAIMED seat (S2 slice, BOARDROOM-2026-09-03-052111 /
   *  CMT-2026-09-03-020): present ⇔ THIS DEVICE recorded a guardian claim for
   *  the backing ticket (src/data/guardianClaim.ts — the claim wire is the
   *  only read that states guardianship). Drives the guardian journey: the
   *  child-named card, the childConfirm step, guardian waiver framing, the
   *  account-holder profile entry (`&guardian=1`), and SUPPRESSES the
   *  parent-vs-ticket eligibility overlay (the PARENT's profile is
   *  account-holder data — never validated against the child's ticket). */
  guardianClaim?: { childName: string; confirmed: boolean }
  /** The held ticket's TYPE name off the /me/holdings wire (Duncan UI order
   *  BOARDROOM-135429: every card identifies WHICH ticket — e.g. "Singles
   *  Solo Pro"). null/absent (older my-api / degraded read / fixture) ⇒ the
   *  type line is omitted honestly. */
  ticketTypeName?: string | null
  /** The holding's org-unit tenancy key off the /me/holdings wire (invitee
   *  org-branding carry, MY-2026-09-10-192731). Lets the wallet resolve
   *  settings' central brand pack for a card whose event is NOT in the
   *  caller's box-office orders read (invitee/guardian holdings). Absent
   *  (older my-api / unresolved server-side) ⇒ the neutral band. */
  orgUnitId?: string | null
  /** Minted bib off the /me/holdings wire (BOARDROOM-2026-09-11-131819) —
   *  the wallet's holder-lane bib source; display-ready, server-padded.
   *  Absent ⇒ the wallet falls back to the purchaser-only box-office bib,
   *  then the em-dash placeholder. */
  bib?: string | null
  name: string
  organiser: GetReadyOrganiser
  /** Display when-line, e.g. `Sat 16 Aug · 09:00`; '' when unknown. */
  when: string
  /** ISO event start (nullable) — the chronological sort key for the home cards
   *  (CMT-121335). Not rendered; `when` is the display string. */
  eventStartsAt?: string | null
  /** The heat start time the participant SELECTED in get-ready ("HH:mm"), from
   *  the my-api readiness payload (CMT-121325). Surfaced on the wallet ticket's
   *  HEAT column — the box-office `t.wave` has no self-select selection.
   *  Optional/`null` when no heat is chosen or an older my-api omits it. */
  selectedHeatTime?: string | null
  /** Guardian card — account holder completes this for a managed child.
   *  TODO(V1.1 kids amendment §12.2). */
  guardian?: boolean
  subject?: string
  /** Card footer note (canon §4.2); omitted when the server provides none. */
  note?: string
  /** POST-EVENT CLOSE (BOARDROOM-2026-08-25-044751): SERVER-computed "event
   *  ended" from the my-api readiness payload (events.ends_at vs now, derived
   *  server-side — the FE never re-derives clock logic). Closed cards render
   *  dimmed + "Event ended" badge, sink to the bottom of the sort, and their
   *  profile/waiver/heat steps show a closed notice instead of CTAs (the
   *  server 409s event_ended on those writes anyway). Optional: absent (older
   *  my-api / no signal) = open — never a guessed close. */
  eventClosed?: boolean
  /** RAW ISO `events.ends_at` off the readiness wire (BOARDROOM-2026-09-01-
   *  175306 item 2): once ends_at + the settle grace passes (isPastEvent —
   *  the ONE shared boundary, src/data/pastEvents.ts) the event is excluded
   *  from the get-ready home, the Tickets badge and the wallet entirely.
   *  During the grace the eventClosed archived read above still applies.
   *  Optional/null (older my-api / degraded read) ⇒ never excluded. */
  eventEndsAt?: string | null
  /** PROFILE-TIME eligibility reject (BOARDROOM-2026-08-28-194055): the
   *  locale-resolved plain copy of a server 422 'ineligible' from this
   *  ticket's profile-complete call — surfaced on the card (amber blocked-
   *  reason bar) so the reject is never silent. Present ONLY while the server
   *  says the profile step is still incomplete AND a recorded 422 exists;
   *  absent otherwise. */
  profileBlock?: string
  /** ELIGIBILITY-MISMATCH overlay (BOARDROOM-2026-08-29-120259 rulings 1–3 +
   *  ADDENDA): the participant's COMPLETE profile fails this ticket's carried
   *  eligibility contract (gender or age axis). Present ⇔ detectProfileMismatch
   *  found one — the card then renders the mismatch banner + the support out
   *  (EligibilityMismatchNotice, with the reported/unreported sub-states)
   *  instead of the bare profileBlock bar, and the profile step row wears the
   *  truthful 'blocked' state ("complete — not valid for this ticket"). */
  eligibilityMismatch?: ProfileEligibilityMismatch
  /** TEEN SELF-HOLDER (VITE_TEEN_SELF_HOLDER_ENABLED): the "Parent/guardian
   *  signature needed" card state for the teen's own kind='self' seat —
   *  Ask / Waiting for {parent} / Signed by {parent}. Absent ⇒ unchanged. */
  teenGuardian?: import('./teenGuardian').TeenGuardianCard
  steps: GetReadyStep[]
}

// ---------------------------------------------------------------------------
// Derivations (canon §2.4 / §4.2) — derived, NEVER stored
// ---------------------------------------------------------------------------

/** Non-system ("yours") steps of an event. */
export const yourSteps = (e: GetReadyEvent): GetReadyStep[] =>
  e.steps.filter(s => !s.system)

/** Issued: the explicit 'ticket' system row is the issuance signal when present
 *  (it mirrors the SERVER's `ready` flag — the same preference receiptNextAction
 *  already applies), falling back to the canon §2.4 every-step rule when no
 *  ticket row exists. The distinction matters since the QUESTIONNAIRE step
 *  landed (SETTINGS-2026-08-25-105204): a questionnaire is a survey, NEVER an
 *  issuance gate (my-api readiness ignores it), so an unanswered questionnaire
 *  must not hide an issued ticket from the wallet. For server-derived step sets
 *  without a questionnaire the two rules are provably equivalent (ticket done ⇔
 *  ready ⇔ profile+waiver+heat done). */
export const isIssued = (e: GetReadyEvent): boolean => {
  // A parent-signing DEFERRED card (item 5, corrected model BOARDROOM-2026-
  // 09-02-152856) is issued into the BUYER's wallet ONLY on the server's
  // explicit purchaser-QR marker (qrAvailable === true — absent on today's
  // wire = false). It otherwise never counts: the card carries no step rows
  // (the buyer cannot read the parent's readiness), so the vacuous
  // every()=true below would fabricate a completion signal — and no
  // signature → no QR anywhere.
  if (e.waiting) return e.waiting.qrAvailable === true
  const ticket = e.steps.find(s => s.key === 'ticket')
  return ticket ? ticket.state === 'done' : e.steps.every(s => s.state === 'done')
}

/** ENDED, for every affordance decision on a Get-ready card: the SERVER's
 *  close verdict (`eventClosed`, BOARDROOM-2026-08-25-044751) OR the event
 *  settled past the ONE shared partition boundary (`isPastEvent` over the
 *  server-carried `eventEndsAt` + settle grace, src/data/pastEvents.ts —
 *  BOARDROOM-2026-09-01-175306 item 2). The composition is the past-stubs
 *  hardening (CMT-2026-09-03-025 / BOARDROOM-2026-09-03-060052, Duncan's
 *  stage screenshot: an ENDED event rendering live Sign buttons + badge
 *  counts): a card past its end can never present an actionable affordance
 *  even if the close signal and the date carry diverge (kill-switch drops
 *  one, an older my-api omits one, a forgotten filter renders a past card).
 *  Mirrors Orders' own `eventClosed || past` defence-in-depth. DEGRADE
 *  (dispatch item 3, the Orders "absent fields never wrongly bury" posture):
 *  BOTH signals absent/unknown ⇒ NOT ended — a live event is never wrongly
 *  locked on a lost signal. */
export const eventEnded = (e: Pick<GetReadyEvent, 'eventClosed' | 'eventEndsAt'>): boolean =>
  !!e.eventClosed || isPastEvent(e.eventEndsAt)

/** Actionable (`now`) step count for one event — NON-SYSTEM ("yours") steps
 *  only. This is the SINGLE derivation behind the amber "! {N} things to do"
 *  pill, the "{done} of {yours} done" badge AND the nav badge, so all three
 *  provably agree (canon §2.4). System ("we do this") rows are never a
 *  participant to-do; the `!s.system` filter enforces that a future
 *  system-`now` row could never inflate the count (audit D5). POST-EVENT
 *  CLOSE (BOARDROOM-2026-08-25-044751): a CLOSED event has NO actionable
 *  steps — the server 409s event_ended on every step write — so it counts 0
 *  here, keeping the card pill AND the nav badge free of dead to-dos. PAST
 *  events count 0 by the SAME composed verdict (eventEnded — CMT-2026-09-03-
 *  025): the belt countNowSteps wears via its filter now also lives at the
 *  single derivation, so a per-card pill can never disagree with the badge. */
export const nowStepCount = (e: GetReadyEvent): number =>
  eventEnded(e) ? 0 : e.steps.filter(s => s.state === 'now' && !s.system).length

/** PAST-EVENT EXCLUSION (BOARDROOM-2026-09-01-175306 item 2): the get-ready
 *  home, the nav badges and the wallet all consume THIS filter (or its
 *  per-event boundary isPastEvent), so a settled past event — ends_at + the
 *  72h grace behind now — carries ZERO affordances anywhere. Missing/absent
 *  eventEndsAt ⇒ visible (an older my-api or a degraded read never buries a
 *  live event). */
export const visibleGetReadyEvents = <T extends GetReadyEvent>(events: T[]): T[] =>
  events.filter(e => !isPastEvent(e.eventEndsAt))

/** Nav badge = total `now` steps across ALL events (canon §2.4). Settled PAST
 *  events are excluded outright (BOARDROOM-2026-09-01-175306 item 2) — belt
 *  over the eventClosed=0 braces, so the badge provably agrees with the
 *  get-ready home even if the close signal and the date carry ever diverge
 *  (e.g. the server kill-switch drops one but not the other). */
export const countNowSteps = (events: GetReadyEvent[]): number =>
  visibleGetReadyEvents(events).reduce((n, e) => n + nowStepCount(e), 0)

/** Card order for the get-ready home + wallet (CMT-121335 chronology + the
 *  BOARDROOM-2026-08-25-044751 post-event SINK). CLOSED events sink to the
 *  BOTTOM regardless of date — an ended event's start is by definition the
 *  earliest instant in the list, so pure chronology pinned it to the TOP (the
 *  ended-event-at-top inversion this fixes). Within each band: soonest start
 *  first; unknown dates last. Stable for equal keys (Array#sort is stable). */
export function compareGetReadyEvents(a: GetReadyEvent, b: GetReadyEvent): number {
  const closedA = a.eventClosed ? 1 : 0
  const closedB = b.eventClosed ? 1 : 0
  if (closedA !== closedB) return closedA - closedB
  const ta = a.eventStartsAt ? Date.parse(a.eventStartsAt) : NaN
  const tb = b.eventStartsAt ? Date.parse(b.eventStartsAt) : NaN
  const va = Number.isNaN(ta) ? Infinity : ta
  const vb = Number.isNaN(tb) ? Infinity : tb
  return va - vb
}

/** Tickets nav badge = number of ISSUED tickets available in the wallet
 *  (BOARDROOM-2026-08-14-100859 UX #4). DERIVED via isIssued from the SAME live
 *  source as the wallet pager (issuedWalletEvents) — never a stored flag, so the
 *  badge count and the wallet always agree. 0 hides the badge. Settled PAST
 *  events are excluded (BOARDROOM-2026-09-01-175306 item 2) — the wallet pager
 *  applies the same visibleGetReadyEvents boundary, keeping the agreement. */
export const countIssued = (events: GetReadyEvent[]): number =>
  visibleGetReadyEvents(events).filter(isIssued).length

/** Card state (canon §4.2 — derived, never stored), counting only NON-system
 *  ("yours") steps:
 *  - 'has-actions'  ≥1 `now` → amber pill `! {N} thing(s) to do`
 *  - 'caught-up'    0 `now`, yours not all done → `✓ Nothing to do right now`
 *                   + next-up line
 *  - 'yours-done'   yours all done, system pending → `✓ You're all set`
 *                   + next-up line (canon §4.3 acceptance, demo card 3)
 *  - 'all-done'     everything incl. system done (issued) → `✓ You're all
 *                   set` + banner */
export type CardState = 'has-actions' | 'blocked' | 'caught-up' | 'yours-done' | 'all-done'
export function cardState(e: GetReadyEvent): CardState {
  if (nowStepCount(e) >= 1) return 'has-actions'
  // ELIGIBILITY MISMATCH (BOARDROOM-2026-08-29-120259 ruling 3): a blocked
  // step with no remaining to-dos must NEVER read as the caught-up "You're up
  // to date" band — the participant does not currently have a valid ticket.
  // Checked AFTER has-actions so real to-dos (waiver/questionnaire stay
  // available per Duncan) keep their pill; the mismatch banner renders on the
  // card independently of this state. ENDED events (closed OR past — the
  // composed eventEnded verdict, CMT-2026-09-03-025) keep their archived read.
  if (!eventEnded(e) && e.steps.some(s => s.state === 'blocked' && !s.system)) return 'blocked'
  if (!yourSteps(e).every(s => s.state === 'done')) return 'caught-up'
  return isIssued(e) ? 'all-done' : 'yours-done'
}

/** Progress over non-system steps ONLY: `{doneN} of {yoursN} done` (§4.2). */
export function cardProgress(e: GetReadyEvent): { doneN: number; yoursN: number; pct: number } {
  const yours = yourSteps(e)
  const doneN = yours.filter(s => s.state === 'done').length
  return {
    doneN,
    yoursN: yours.length,
    pct: yours.length ? Math.round((doneN / yours.length) * 100) : 0,
  }
}

/** Status pill (canon §4.2 templates — verbatim [chrome]):
 *  `! {N} thing(s) to do` / `✓ Nothing to do right now` / `✓ You're all set`. */
export function statusPill(e: GetReadyEvent, tr: TranslateFn = enT): { icon: '!' | '✓'; text: string; tone: 'warn' | 'ok' } {
  const state = cardState(e)
  if (state === 'has-actions') {
    const need = nowStepCount(e)
    return {
      icon: '!',
      text: need === 1 ? tr('getReady.pill.todo.one') : tr('getReady.pill.todo.other', { n: need }),
      tone: 'warn',
    }
  }
  // Mismatch pill: truthful amber state, not an action nag — it reads the
  // same before and after the support report (the ADDENDUM's no-re-nag rule
  // lives in the banner's sub-states, not here).
  if (state === 'blocked') return { icon: '!', text: tr('getReady.pill.mismatch'), tone: 'warn' }
  if (state === 'caught-up') return { icon: '✓', text: tr('getReady.pill.caughtUp'), tone: 'ok' }
  return { icon: '✓', text: tr('getReady.pill.allSet'), tone: 'ok' }
}

/** Next-up line `Next: {label} — {when}.` — first not-done step; `when` is the
 *  step meta with `Automatic · ` stripped and `Opens ` lowercased (§4.2).
 *  Shown for caught-up and yours-done cards (canon §4.3 acceptance: demo card
 *  3 shows next-up; the all-done cards 4–5 show the banner instead). */
export function nextUpLine(e: GetReadyEvent, tr: TranslateFn = enT): string | null {
  const state = cardState(e)
  if (state !== 'caught-up' && state !== 'yours-done') return null
  const n = e.steps.find(s => s.state !== 'done')
  if (!n) return null
  const when = (n.meta || '').replace(/^Automatic · /, '').replace(/^Opens /, 'opens ')
  return when
    ? tr('getReady.next.withWhen', { label: n.label, when })
    : tr('getReady.next.bare', { label: n.label })
}

/** Card footer issuance line (canon §4.2, get-ready binary frames). While the
 *  REQUIRED heat step is still pending the card carries the verbatim [chrome]
 *  line explaining when the ticket appears. DERIVED from the steps — never a
 *  per-event server `note` (the live path never supplies one, audit D4). A
 *  server-provided `note`, when present, still wins in the card render. */
export function heatIssuanceNote(e: GetReadyEvent, tr: TranslateFn = enT): string | null {
  const heat = e.steps.find(s => s.key === 'heat')
  if (!heat || heat.state === 'done') return null
  // MODE-CONDITIONAL wording (Duncan get-ready copy bug): a system-allocated
  // heat row (`system: true` — the organiser assigns the start time, no
  // participant Choose step) must read "once heats are allocated", NEVER "once
  // you have chosen your heat". Self-select keeps the canon [chrome] wording.
  return heat.system
    ? tr('getReady.note.heatIssuance.alloc')
    : tr('getReady.note.heatIssuance')
}

// ---------------------------------------------------------------------------
// Wizard / waiver entry mapping (canon §4.3 [E2E contract])
// ---------------------------------------------------------------------------

/**
 * Route for a step's user action, per the canon §4.3 entry contract:
 *  - profile  Start/Edit → wizard step 1; Resume → first incomplete step
 *    (`pAt` — the caller resolves it via wizardModel.firstIncompleteStep and
 *    passes it as `resumeStep`).
 *  - emergency Add → the SAME profile wizard at step 4.
 *  - waiver   Sign → the waiver flow; View → the receipt.
 *  - system steps NEVER have an action (returns null).
 *  - heat: a REQUIRED participant step in the OnePulse canon — the participant
 *    chooses a start time (label "Heat selection", CTA "Choose"). It is NOT
 *    system-automatic; an earlier note here asserted the OPPOSITE ("canon
 *    models heat as system-automatic / TEMPORARY DEVIATION") and was wrong —
 *    corrected per the CMT-2026-08-09-043 canon audit (D1). The "Choose" CTA
 *    routes to the heat-select surface (POST /me/tickets/:id/heat); a `now`
 *    step MUST have a wired action (canon §4.3 must-nots), which this satisfies.
 *
 * TODO(V1.1 kids amendment §12.2): child-subject steps open the wizard with
 * `subject = childKey` (/get-ready/profiles/:childKey/step-N) — deferred.
 */
export function stepEntryPath(
  eventId: string,
  step: GetReadyStep,
  opts: { resumeStep?: number; guardian?: boolean; ticketId?: string } = {},
): string | null {
  if (step.system || !step.cta) return null
  // Profile-wizard entries carry the EVENT SCOPE as `?event={eventId}`
  // (CMT-2026-08-29-022): the wizard applies that event's question toggles
  // (T-shirt size / language) from settings' resolved participant-config.
  // Query param, not a route change — the account-entry wizard routes stay as
  // ratified in canon §1.2; no param ⇒ ask everything (today's behaviour).
  // GUARDIAN-CLAIMED entry (S2 slice, CMT-2026-09-03-020) adds `&guardian=1`:
  // the wizard then renders the account-holder step-1 copy (never bib/results
  // for the parent's name). No flag ⇒ today's behaviour, byte-identical.
  const eventScope = `?event=${encodeURIComponent(eventId)}${opts.guardian ? '&guardian=1' : ''}`
  switch (step.key) {
    case 'profile':
      if (step.entryStep) return `/get-ready/profile/step-${step.entryStep}${eventScope}`
      return step.cta === 'Resume'
        ? `/get-ready/profile/step-${opts.resumeStep ?? 1}${eventScope}`
        : `/get-ready/profile/step-1${eventScope}`
    case 'emergency':
      return `/get-ready/profile/step-4${eventScope}`
    case 'childConfirm':
      // SERVER-BACKED child details (boardroom 2026-10-01, quality F1): the
      // child's identity lives on the seat's ticket_holders row for BOTH
      // lanes (kind='child' and guardian-claimed), so the step opens the
      // ticket-keyed form — /get-ready/child-details/{ticketId} — which
      // loads + saves via my-api (deep-linkable; no route state, no
      // device-local record needed). A row carrying a managed-child SUBJECT
      // key is a LEGACY local-only seat the server holds no child for: it
      // keeps the managed wizard. Never /profile.
      if (opts.ticketId && !step.subject) return `/get-ready/child-details/${encodeURIComponent(opts.ticketId)}`
      if (step.subject) return `/profile/children/${encodeURIComponent(step.subject)}/step-1`
      // No ticket in hand (fixture renders / the waiver-receipt next action):
      // the event-keyed legacy entry, which forwards to the ticket-keyed form
      // when its route state carries the ticket.
      return `/get-ready/child-confirm/${eventId}`
    case 'waiver':
      return step.cta === 'View'
        ? `/get-ready/waiver/${eventId}/receipt`
        : `/get-ready/waiver/${eventId}/doc-1`
    case 'heat':
      // Heat-select surface for the required participant heat choice (D1).
      return `/get-ready/heat/${eventId}`
    case 'questionnaire':
      // Event questionnaire flow (SETTINGS-2026-08-25-105204 — settings' /svc
      // seam via the my-api BFF, agreed contract MY-2026-08-25-054534).
      return `/get-ready/questionnaire/${eventId}`
    default:
      return null
  }
}

// ---------------------------------------------------------------------------
// Waiver-receipt next action (CMT-2026-08-13-121331)
// ---------------------------------------------------------------------------

/**
 * The context-aware NEXT ACTION offered on the waiver receipt, derived from the
 * SAME server readiness step rows as Get ready (single source of truth — never
 * hardcoded here). After signing, exactly one of:
 *  - 'step'   a further user-doable step remains (`now`, non-system, not the
 *             waiver itself) → the button routes to that step's entry path.
 *  - 'ticket' no further user step AND the ticket is issued → the button routes
 *             to the wallet/QR view for the backing ticket.
 *  - 'pending' no further user step and the ticket is NOT yet issued (e.g. the
 *             organiser still has to allocate a heat) → no button, a clear
 *             status message instead. `pendingLabel` is the first not-yet-done
 *             step's label (for an honest message), or null when unknown.
 */
export type ReceiptNextAction =
  | { kind: 'step'; step: GetReadyStep; path: string }
  | { kind: 'ticket'; path: string }
  | { kind: 'pending'; pendingLabel: string | null }

export function receiptNextAction(
  eventId: string,
  steps: GetReadyStep[] | null | undefined,
  ticketPassId?: string,
): ReceiptNextAction {
  const list = steps ?? []
  // A further user-doable step: a non-system `now` step other than the waiver
  // (which is signed at this point — and live readiness may still lag). Only
  // count it if it has a wired entry path (a `now` step always should).
  for (const s of list) {
    if (s.system || s.state !== 'now' || s.key === 'waiver') continue
    const path = stepEntryPath(eventId, s)
    if (path) return { kind: 'step', step: s, path }
  }
  // No further user step. Issued ⇔ the ticket step is done (issuance mints the
  // QR); fall back to "every step done" when no explicit ticket row exists.
  const ticket = list.find((s) => s.key === 'ticket')
  const issued = ticket ? ticket.state === 'done' : list.length > 0 && list.every((s) => s.state === 'done')
  if (issued) {
    return { kind: 'ticket', path: ticketPassId ? `/tickets/${ticketPassId}` : '/tickets' }
  }
  // Nothing the participant can do, and no ticket yet — honest waiting state.
  const pending = list.find((s) => s.state !== 'done')
  return { kind: 'pending', pendingLabel: pending?.label ?? null }
}

// ---------------------------------------------------------------------------
// Server seam — per-event step data (Ruling C: server-derived readiness)
// ---------------------------------------------------------------------------

/*
 * CANONICAL SHAPE (PROPOSED, for infra/my-api — not yet served): a single
 * server-derived per-event step payload,
 *
 *   GET /me/tickets/{ticketId}/get-ready
 *   → { eventId, steps: GetReadyStep[] }   // states/system/cta/meta all
 *                                          // DERIVED server-side, no stored
 *                                          // done/issued flag (Ruling C)
 *
 * UNTIL that endpoint ships, fetchEventSteps() below keeps the same seam
 * signature but adapts the CURRENT my-api server readiness result
 * (GET /me/tickets/{id}/readiness → {profileComplete, waiverComplete,
 * heatComplete, ready}) into canon step rows. The mapping is pure display
 * adaptation of what the server said — nothing is persisted client-side and
 * the retired markProfileComplete client-flag pattern is NOT used.
 */

/** Pure adapter: current my-api per-ticket readiness → canon §4.3 step rows.
 *  Step SET reflects what today's server actually reports (per-event step
 *  sets are server-driven — omitting steps the server has no data for, e.g.
 *  emergency/questionnaire, is canon-compliant; no rows are invented). */
/** Heat-selection policy for an event (CMT-2026-08-10-012/018). Sourced from a
 *  settings-exposed signal (heat_sel exposure on the event/ticket-type) — NOT a
 *  per-event hardcode here. `self-select` (OnePulse): the participant chooses a
 *  start time (Choose CTA). `system-allocated` (Apex): the organiser assigns the
 *  heat, so there is NO participant Choose step — a "we do this" allocation row
 *  is shown instead. `box-office` (P1 BOARDROOM-2026-09-09-171256): the BUYER
 *  picked the heat at checkout — nothing to do in get-ready and nobody "assigns"
 *  anything, so the step reads "Chosen at checkout" (with the time when known),
 *  never the auto copy. Defaults to `self-select` (today's server behaviour)
 *  when the wire carries no signal. */
export type HeatPolicy = 'self-select' | 'system-allocated' | 'box-office'

export function stepsFromTicketReadiness(
  r: Readiness,
  opts: { organiserName?: string; heatPolicy?: HeatPolicy; checkoutHeatTime?: string | null; tr?: TranslateFn } = {},
): GetReadyStep[] {
  const org = opts.organiserName
  const tr = opts.tr ?? enT
  const heatPolicy: HeatPolicy = opts.heatPolicy ?? 'self-select'
  // The heat time already known OUTSIDE the self-select flow: the buyer's
  // box-office checkout pick (or an organiser allocation) rides the wallet
  // ticket's wave — threaded in by the screen (P1 BOARDROOM-2026-09-09-171256).
  const checkoutTime = opts.checkoutHeatTime ?? null
  // Policy-driven heat row (CMT-2026-08-10-018): self-select shows a participant
  // "Choose your heat" action; system-allocated shows a "we allocate" system row
  // with no CTA (canon §4.3 must-nots for system steps); box-office (P1
  // BOARDROOM-2026-09-09-171256) reflects the CHECKOUT pick — done, no CTA,
  // never "we assign" (nobody will) and never a Choose CTA (already chosen).
  const heatStep: GetReadyStep =
    heatPolicy === 'box-office'
      ? r.heatComplete || checkoutTime
        ? {
            key: 'heat',
            label: tr('getReady.step.heat.label'),
            meta: checkoutTime
              ? tr('getReady.step.heat.checkoutTime', { time: checkoutTime })
              : tr('getReady.step.heat.checkout'),
            state: 'done',
          }
        : {
            // HONESTY POLISH (BOARDROOM-2026-09-10-132427 / the 130105 Duncan-
            // live class): a box-office event whose unit carries NO heat stamp
            // (heatComplete false, no wallet wave) must NOT claim "Chosen at
            // checkout" — nothing was chosen because checkout offered nothing
            // (unmaterialized heat schedule). Render the truth: an OPEN system
            // row "Heat pending" — resolved by the organiser side (schedule
            // materialization + stamp), never a participant CTA (no picker
            // exists in box-office mode).
            key: 'heat',
            label: tr('getReady.step.heat.label'),
            meta: tr('getReady.step.heat.boxPending'),
            state: 'soon',
            system: true,
          }
      : heatPolicy === 'system-allocated'
      ? {
          key: 'heat',
          label: tr('getReady.step.heatAlloc.label'),
          // A KNOWN start time makes "We assign your start time" factually
          // wrong whatever the mode — a box-office event mis-presenting as
          // system-allocate on the binary wire (until settings emits the third
          // value) still renders the truth when the checkout pick is known.
          meta: r.heatComplete
            ? tr('getReady.step.heatAlloc.done')
            : checkoutTime
              ? tr('getReady.step.heatAlloc.set', { time: checkoutTime })
              : tr('getReady.step.heatAlloc.pending'),
          state: r.heatComplete || checkoutTime ? 'done' : 'soon',
          system: true,
        }
      : {
          key: 'heat',
          // Heat is a REQUIRED participant step in the OnePulse self-select canon
          // (the participant chooses a start time). Pending → amber "!" marker +
          // filled-teal "Choose" CTA + verbatim meta "Choose your start time".
          label: tr('getReady.step.heat.label'),
          // Done meta carries the CHOSEN TIME when the wire has it (V1.9.1
          // README §1: metas carry operational data, "Wave 3 · 07:40"-style —
          // never a bare restatement). selectedHeatTime absent (older my-api /
          // no time on the heat) ⇒ the plain 'Chosen', nothing fabricated.
          meta: r.heatComplete
            ? r.selectedHeatTime
              ? tr('getReady.step.heat.chosenTime', { time: r.selectedHeatTime })
              : tr('getReady.step.heat.chosen')
            : tr('getReady.step.heat.pending'),
          state: r.heatComplete ? 'done' : 'now',
          // Completed-but-editable → the Edit affordance (README §D): reopens
          // the heat flow preselected, free move to any available heat.
          cta: r.heatComplete ? 'Edit' : 'Choose',
        }
  // The profile is otherwise complete and ONLY the event's required fitness
  // question is outstanding (my-api profileBlockedOnlyByFitness): say exactly
  // that, and Start lands on the wizard's fitness step — never the generic
  // "Details missing" (prod 92d9fb7c, boardroom 2026-10-01).
  const fitnessOnly = !r.profileComplete && r.profileBlockedOnlyByFitness === true
  return [
    {
      key: 'profile',
      label: tr('getReady.step.profile.label'),
      // Pending meta is canon verbatim (D2). Done meta 'Completed' is unseen in
      // the binary frames (TBD-UNSEEN) — kept, not invented.
      meta: r.profileComplete
        ? tr('getReady.step.profile.done')
        : fitnessOnly
          ? tr('getReady.step.profile.fitnessPending')
          : tr('getReady.step.profile.pending'),
      state: r.profileComplete ? 'done' : 'now',
      // done + wired flow → outline Edit (opens the wizard with data, §4.3).
      cta: r.profileComplete ? 'Edit' : 'Start',
      ...(fitnessOnly ? { entryStep: FITNESS_WIZARD_STEP } : {}),
    },
    {
      key: 'waiver',
      label: tr('getReady.step.waiver.label'),
      // BOARDROOM-124635 (Duncan launch decision): the server signals a
      // NON-BLOCKING waiver by returning ready:true while waiverComplete:false
      // (normally ready implies the waiver is signed, so this combination
      // uniquely means "waiver waived" — WAIVER_REQUIRED=false on my-api). When
      // waived we render the step NON-BLOCKING (state 'done' so the ticket
      // issues + QR mints) but keep it VISIBLE + signable (the 'Sign' CTA + the
      // waiver flow stay reachable, preserving legal intent). Re-gates on its own
      // the moment my-api flips WAIVER_REQUIRED back (ready:false → 'now' again).
      meta: r.waiverComplete
        ? tr('getReady.step.waiver.done')
        : r.ready
          ? tr('getReady.step.waiver.optional')
          : org ? tr('getReady.step.waiver.required', { org }) : tr('getReady.step.waiver.requiredNoOrg'),
      state: r.waiverComplete || r.ready ? 'done' : 'now',
      // Keep the Sign CTA available whenever it is unsigned (required OR
      // optional) so the waiver text/flow is never hidden.
      cta: r.waiverComplete ? undefined : 'Sign',
    },
    // Spectator ruling: no heat (and so no start time) for a spectator ticket.
    ...(r.ticketKind === 'spectator' ? [] : [heatStep]),
    {
      key: 'ticket',
      // System ("we do this") step — issuance follows the heat step. The
      // pending meta is MODE-CONDITIONAL: self-select reads the canon verbatim
      // "After you pick a heat" (D3); system-allocated reads "After heats are
      // allocated" (the participant never picks a heat, so the pick-a-heat
      // wording is wrong for allocation events — Duncan get-ready copy bug).
      label: tr('getReady.step.ticket.label'),
      // MODE-CONDITIONAL pending meta: self-select "After you pick a heat";
      // system-allocated "After heats are allocated"; box-office (the heat is
      // ALREADY set at checkout — neither wording is true) "After your
      // remaining steps are complete".
      meta: r.ready
        ? tr('getReady.step.ticket.done')
        : tr(heatPolicy === 'system-allocated'
            ? 'getReady.step.ticket.pending.alloc'
            : heatPolicy === 'box-office'
              ? 'getReady.step.ticket.pending.box'
              : 'getReady.step.ticket.pending'),
      state: r.ready ? 'done' : 'soon',
      system: true,
    },
  ]
}

/** Bridge settings' per-event heat signal (folded onto the readiness payload by
 *  my-api, CMT-2026-08-10-018) to the FE `HeatPolicy`. Settings emits
 *  `heat_sel` (BOOLEAN) + `heat_policy` (`self-select`|`system-allocate`); the FE
 *  spells the allocated case `system-allocated`. Returns `undefined` when the
 *  server sends NO signal (older my-api, or the view not yet applied) so the
 *  caller default / `stepsFromTicketReadiness`'s self-select default holds —
 *  never guesses a policy from absence. */
export function heatPolicyFromReadiness(r: Readiness): HeatPolicy | undefined {
  // The EXPLICIT policy string outranks the heat_sel boolean: a box-office
  // event carries heat_sel=false (no in-app picker) — checking the boolean
  // first would collapse it into system-allocated, the exact P1 BOARDROOM-
  // 2026-09-09-171256 mis-copy. Deploy-ahead: lights up when settings emits it.
  if (r.heatPolicy === 'box-office') return 'box-office'
  if (r.heatSel === false || r.heatPolicy === 'system-allocate') return 'system-allocated'
  if (r.heatSel === true || r.heatPolicy === 'self-select') return 'self-select'
  return undefined
}

/**
 * QUESTIONNAIRE step derivation (SETTINGS-2026-08-25-105204; contract
 * MY-2026-08-25-054534). Server-authoritative, from the my-api BFF read
 * (GET /me/events/{eventId}/questionnaire → settings' /svc resolved payload):
 *  - active && show && complete   → a 'done' step (no Edit/View flow in v1)
 *  - active && show && !complete  → a 'now' step with a Start CTA
 *    where complete = questionnaireComplete: every REQUIRED NON-fitness
 *    question answered per its OWN per-question signal (settings #471/#630
 *    `answered`). The resolved-level `submitted` is NEVER read: it is
 *    some(answered), and settings #630 carries the participant's latest
 *    fitness answer from ANY event onto the fitness row — so it would mark the
 *    event questionnaire done off a fitness answer given elsewhere. No
 *    required non-fitness question ⇒ done (nothing required left).
 *  - The fitness_self row is EXCLUDED (boardroom ruling 2026-10-01: a
 *    PROFILE-LEVEL field asked by the profile wizard). A questionnaire whose
 *    ONLY question is fitness_self → NULL: no Event questionnaire step.
 *  - !active or !show (or no data — a failed/absent BFF read) → NULL: the step
 *    is ABSENT entirely, no placeholder row (never fabricate a to-do).
 */
export function questionnaireStepFromStatus(
  q: QuestionnaireStatus | null | undefined,
  tr: TranslateFn = enT,
): GetReadyStep | null {
  if (!q || !q.active || !q.show) return null
  if (eventQuestionnaireQuestions(q.questions).length === 0) return null
  if (questionnaireComplete(q)) {
    return {
      key: 'questionnaire',
      label: tr('getReady.step.questionnaire.label'),
      meta: tr('getReady.step.questionnaire.done'),
      state: 'done',
    }
  }
  return {
    key: 'questionnaire',
    label: tr('getReady.step.questionnaire.label'),
    meta: tr('getReady.step.questionnaire.pending'),
    state: 'now',
    cta: 'Start',
  }
}

/**
 * "QUESTIONS" step from the READINESS read (boardroom ruling 2026-10-20 option
 * 2, PROGRAMME-RULE-SELF-SERVICE-COMPLETE-DEFINITION-V1). my-api reports, off
 * the SAME settings /resolved body as the fitness re-derive, whether the event
 * emits any REQUIRED non-fitness question for this participant
 * (otherQuestionsRequired) and whether all of them are answered
 * (otherQuestionsComplete). Settings confirmed that per-question required/
 * answered is authoritative.
 *  - otherQuestionsRequired === true + complete === true  → 'done' row
 *  - otherQuestionsRequired === true + complete === false → 'now' row, Start
 *    CTA into the existing questionnaire flow (same 'questionnaire' key, so
 *    stepEntryPath routes it to /get-ready/questionnaire/{eventId} and the
 *    child "(for {name})" marker applies)
 *  - anything else (false, null = unknown, absent = older my-api) → NULL: no
 *    Questions row from readiness.
 * INFORMATIONAL for the QR: it never touches `ready`, the ticket (issuance)
 * row, or isIssued — the QR rule is unchanged. Like the event-questionnaire
 * row it is a participant ("yours") step, so it counts in the card's
 * "{done} of {yours}" progress and the to-do pill.
 */
export function questionsStepFromReadiness(
  r: Pick<Readiness, 'otherQuestionsRequired' | 'otherQuestionsComplete'> | null | undefined,
  tr: TranslateFn = enT,
): GetReadyStep | null {
  if (!r || r.otherQuestionsRequired !== true) return null
  if (r.otherQuestionsComplete === true) {
    return { key: 'questionnaire', label: tr('getReady.step.questions.label'), meta: tr('getReady.step.questions.done'), state: 'done' }
  }
  if (r.otherQuestionsComplete === false) {
    return {
      key: 'questionnaire',
      label: tr('getReady.step.questions.label'),
      meta: tr('getReady.step.questions.pending'),
      state: 'now',
      cta: 'Start',
    }
  }
  return null // unknown — no step
}

/** Insert the questionnaire step (when one derives) into a server step-row set:
 *  after profile/waiver, before the heat row (the seed-demo canon position) —
 *  falling back to before the system ticket row, else the end. No-op (same
 *  array) when the derivation is null — the step set stays exactly the server's
 *  (omitting steps with no data is canon-compliant; no rows are invented).
 *  `r` (the readiness payload, optional): when it derives a "Questions" row
 *  (questionsStepFromReadiness) that row is used INSTEAD of the BFF-derived
 *  event-questionnaire row — one questionnaire row per card, never two. When
 *  readiness says nothing (unknown / exempt seat / older my-api) the BFF
 *  derivation applies exactly as before. */
export function withQuestionnaireStep(
  steps: GetReadyStep[],
  q: QuestionnaireStatus | null | undefined,
  tr: TranslateFn = enT,
  r?: Pick<Readiness, 'otherQuestionsRequired' | 'otherQuestionsComplete'> | null,
): GetReadyStep[] {
  const step = questionsStepFromReadiness(r, tr) ?? questionnaireStepFromStatus(q, tr)
  if (!step) return steps
  const at = (() => {
    const heat = steps.findIndex(s => s.key === 'heat')
    if (heat !== -1) return heat
    const ticket = steps.findIndex(s => s.key === 'ticket')
    return ticket !== -1 ? ticket : steps.length
  })()
  return [...steps.slice(0, at), step, ...steps.slice(at)]
}

/**
 * GUARDIAN-CLAIMED seat step overlay (S2 slice, BOARDROOM-2026-09-03-052111 /
 * CMT-2026-09-03-020) — and, since dispatch BOARDROOM-2026-09-07-103021, the
 * shared core of the DECLARED-PERMISSION managed-child composition (see
 * applyDeclaredPermissionChild below). Guardian variant applied ONLY when the
 * device holds a guardian-claim record for the backing ticket:
 *  - INSERTS the childConfirm step directly after the profile row (fallback:
 *    before the waiver row, else the front): child name confirmed + DOB and
 *    gender collected — the PARENT is the authority (neither is captured at
 *    assign time on the no-permission path). Its done-state is the LOCAL
 *    record's confirmed flag: the S1 wire has no child-details read/write
 *    (POST /claim + readiness keep their current shapes), so nothing server-
 *    side can carry it — an honest device-local step, never presented as
 *    server truth.
 *  - REFRAMES the profile row's pending meta as account-holder data (the
 *    parent's details are NOT bib/results copy) and the waiver row as the
 *    guardian signature — the ruled sentence "Sign the waiver for {child}'s
 *    ticket"; the SUBJECT is the child, the signer is the parent.
 *  - WHO-IS-THIS-ABOUT markers (BOARDROOM-2026-09-05-101136 — Duncan's live
 *    test: "Your profile" on a For-{child} card read as the CHILD's profile):
 *    EVERY row states its subject. The profile label names the PARENT
 *    ("Your profile (you, the parent or guardian)"); rows whose label doesn't
 *    already carry the child's name (childConfirm/waiver do natively) wear
 *    the generic "(for {child})" suffix — an EXPLICIT allowlist (heat,
 *    ticket, questionnaire), LABEL only (state/meta/cta untouched). Unknown/
 *    future keys pass through UNTOUCHED: a future 'emergency' row edits the
 *    PARENT's data, and a server-emitted 'childConfirm' would duplicate the
 *    native child naming — never a blind catch-all suffix. Guardian mode
 *    only: a self-claimed seat never enters this overlay.
 */
const FOR_CHILD_KEYS = new Set(['heat', 'ticket', 'questionnaire'])
/** DECLARED-PERMISSION variant of the allowlist (dispatch BOARDROOM-2026-09-
 *  07-103021): unlike the guardian-claimed journey (where a future 'emergency'
 *  row edits the PARENT's data), the declared-permission managed seat routes
 *  emergency to the CHILD's managed wizard step 4 (ruling BOARDROOM-2026-09-
 *  02-150714 item 3) — so it wears the child marker there. */
const DECLARED_FOR_CHILD_KEYS = new Set([...FOR_CHILD_KEYS, 'emergency'])

/** Shared two-identity managed-child composition (the settled guardian-invited
 *  pattern): parent-profile row reframed as the ACCOUNT HOLDER's own, the
 *  child-details (childConfirm) row inserted after it, guardian waiver
 *  framing, and who-is-this-about markers on the allowlisted rows. The
 *  guardian-claimed and declared-permission variants differ ONLY in the
 *  marker allowlist and the child-details subject key (see the exports). */
function applyManagedChildComposition(
  steps: GetReadyStep[],
  rec: { childName: string; confirmed: boolean; optional?: boolean },
  forChildKeys: ReadonlySet<string>,
  subjectKey: string | null,
  tr: TranslateFn,
): GetReadyStep[] {
  const name = rec.childName.trim()
  // The generic child-subject marker for rows whose label doesn't natively
  // name the child — honest name-less fallback, never an invented name.
  const forChild = (label: string) =>
    name
      ? tr('getReady.step.labelForChild', { label, name })
      : tr('getReady.step.labelForChildNoName', { label })
  // IDEMPOTENCY: skip a label that already carries the computed suffix so a
  // double application (re-derive over already-overlaid rows) is a no-op.
  const childSuffix = forChild('')
  const reframed: GetReadyStep[] = steps.map((s) => {
    if (s.key === 'profile') {
      return {
        ...s,
        // The parent marker rides done AND pending — the who-question doesn't
        // stop applying once the step completes. Meta reframes pending only
        // (done keeps its honest 'Completed').
        label: tr('getReady.step.profile.labelGuardian'),
        ...(s.state !== 'done' ? { meta: tr('getReady.step.profile.guardianPending') } : {}),
      }
    }
    if (s.key === 'waiver') {
      return {
        ...s,
        label: name
          ? tr('getReady.step.waiver.labelGuardian', { name })
          : tr('getReady.step.waiver.labelGuardianNoName'),
        ...(s.state !== 'done' ? { meta: tr('getReady.step.waiver.guardianMeta') } : {}),
      }
    }
    if (forChildKeys.has(s.key) && !s.label.endsWith(childSuffix)) {
      return { ...s, label: forChild(s.label) }
    }
    return s // unknown/future keys (and already-suffixed rows) pass UNTOUCHED
  })
  // A childConfirm row already present (double application / a future
  // server-emitted row) is never duplicated — the existing row stands.
  if (reframed.some((s) => s.key === 'childConfirm')) return reframed
  // NON-BLOCKING PROMPT (my-api V25 age-band split, ruling 1 on #241): on a
  // NO-band ticket the server does not gate the seat on the child's details
  // (childDetailsBlocking=false), so the row is an invitation, not a to-do —
  // the waiver-optional precedent: state 'done' (never counts toward "N
  // things to do", never holds the ticket back) but the row + its 'Add' CTA
  // stay visible and reach the same ChildConfirm flow.
  const prompt = !rec.confirmed && rec.optional === true
  const childStep: GetReadyStep = {
    key: 'childConfirm',
    // Declared-permission rows carry the managed-child profile key so the
    // step routes to THAT CHILD's wizard (stepEntryPath); guardian-claimed
    // rows carry none (the child-confirm flow is their identity home).
    ...(subjectKey ? { subject: subjectKey } : {}),
    label: prompt
      ? name
        ? tr('getReady.step.childConfirm.prompt.label', { name })
        : tr('getReady.step.childConfirm.prompt.labelNoName')
      : name
        ? tr('getReady.step.childConfirm.label', { name })
        : tr('getReady.step.childConfirm.labelNoName'),
    meta: rec.confirmed
      ? tr('getReady.step.childConfirm.done')
      : prompt
        ? tr('getReady.step.childConfirm.prompt.meta')
        : tr('getReady.step.childConfirm.pending'),
    state: rec.confirmed || prompt ? 'done' : 'now',
    cta: rec.confirmed ? 'Edit' : prompt ? 'Add' : 'Start',
  }
  const at = (() => {
    const profile = reframed.findIndex((s) => s.key === 'profile')
    if (profile !== -1) return profile + 1
    const waiver = reframed.findIndex((s) => s.key === 'waiver')
    return waiver !== -1 ? waiver : 0
  })()
  return [...reframed.slice(0, at), childStep, ...reframed.slice(at)]
}

export function applyGuardianClaim(
  steps: GetReadyStep[],
  rec: { childName: string; confirmed: boolean; optional?: boolean },
  tr: TranslateFn = enT,
): GetReadyStep[] {
  return applyManagedChildComposition(steps, rec, FOR_CHILD_KEYS, null, tr)
}

/**
 * DECLARED-PERMISSION managed-child step composition (dispatch BOARDROOM-
 * 2026-09-07-103021, Duncan-settled design). Applied to a seat the account
 * holder manages for a child under an assign-time declared permission (a
 * local assign-store child record, or the wire's kind='child') — the card
 * previously kept the unrelativised self labels ("Your profile / Sign your
 * waiver") under a "For {child}" header, which Duncan misread as his own
 * profile. Same two-identity composition as the settled guardian-invited
 * card, with two variant differences:
 *  - the child-details row is SUBJECT-KEYED to the managed child profile
 *    (`rec.childKey`) so it edits THEIR wizard (/profile/children/{key}),
 *    never the guardian-claim child-confirm flow;
 *  - `confirmed` is the LOCAL managed profile's identity completeness (name +
 *    DOB + gender — the same facts resolveChildResumeStep walks), an honest
 *    device-local state (my-api has no child-details read), mirroring the
 *    guardian record's confirmed flag;
 *  - the 'emergency' row wears the child marker (see DECLARED_FOR_CHILD_KEYS).
 * UI composition/labels/routing ONLY — no new gates (hold d21): every state
 * still comes from the server readiness rows or pre-existing local records.
 */
export function applyDeclaredPermissionChild(
  steps: GetReadyStep[],
  rec: { childName: string; confirmed: boolean; childKey?: string | null },
  tr: TranslateFn = enT,
): GetReadyStep[] {
  return applyManagedChildComposition(steps, rec, DECLARED_FOR_CHILD_KEYS, rec.childKey ?? null, tr)
}

/** Server-seam fetch: per-event step rows for one held ticket. Signature is
 *  stable; the body swaps to the canonical get-ready endpoint when my-api
 *  ships it (see seam note above). The heat policy is derived from the SAME
 *  readiness payload (no extra round-trip, CMT-2026-08-10-018 option-b); an
 *  explicit `opts.heatPolicy` still wins for tests/overrides. */
export async function fetchEventSteps(
  idToken: string,
  base: string,
  ticketId: string,
  opts: { organiserName?: string; heatPolicy?: HeatPolicy; checkoutHeatTime?: string | null; tr?: TranslateFn } = {},
  fetchImpl: typeof fetch = fetch,
): Promise<{
  steps: GetReadyStep[]
  eventStartsAt: string | null
  selectedHeatTime: string | null
  eventClosed: boolean
  eventEndsAt: string | null
  eligibility: TicketEligibility | null
  raceDay: string | null
  childDetailsComplete: boolean | null
  childDetailsBlocking: boolean | null
  ticketKind: 'standard' | 'spectator'
  otherQuestionsRequired: boolean | null
  otherQuestionsComplete: boolean | null
  waiverComplete?: boolean
  guardianSignatureRequired?: boolean
  guardianRequest?: import('./myApi').GuardianRequestView | null
}> {
  const readiness = await fetchReadiness(idToken, base, ticketId, fetchImpl)
  const heatPolicy = opts.heatPolicy ?? heatPolicyFromReadiness(readiness)
  // eventStartsAt (ISO, nullable) rides alongside the steps so the home can order
  // event cards chronologically (soonest first) — CMT-121335. selectedHeatTime
  // ("HH:mm", nullable) rides too so the wallet ticket can show a heat chosen in
  // get-ready — CMT-121325. eventClosed (BOARDROOM-2026-08-25-044751) is the
  // SERVER-computed close signal — absent (older my-api) reads as open.
  // eligibility/raceDay (BOARDROOM-2026-08-28-194055 item 4) ride too so the
  // per-ticket fallback path can map reject copy — absent (older my-api) = null.
  return {
    steps: stepsFromTicketReadiness(readiness, { ...opts, heatPolicy }),
    eventStartsAt: readiness.eventStartsAt ?? null,
    selectedHeatTime: readiness.selectedHeatTime ?? null,
    eventClosed: readiness.eventClosed ?? false,
    // Raw ends_at carry (BOARDROOM-2026-09-01-175306 item 2) — the past-event
    // exclusion boundary; absent (older my-api) = null = never excluded.
    eventEndsAt: readiness.eventEndsAt ?? null,
    eligibility: readiness.eligibility ?? null,
    raceDay: readiness.raceDay ?? null,
    // Guardian child details (my-api V25) — server truth for the childConfirm
    // step; null = no server signal (non-guardian seat / older my-api).
    childDetailsComplete: readiness.childDetailsComplete ?? null,
    childDetailsBlocking: readiness.childDetailsBlocking ?? null,
    ticketKind: readiness.ticketKind === 'spectator' ? 'spectator' : 'standard',
    // Informational "Questions" row (ruling 2026-10-20 option 2) — absent
    // (older my-api) = null = no readiness-derived row.
    otherQuestionsRequired: readiness.otherQuestionsRequired ?? null,
    otherQuestionsComplete: readiness.otherQuestionsComplete ?? null,
    // TEEN SELF-HOLDER carry (read only behind the FE flag).
    waiverComplete: readiness.waiverComplete,
    guardianSignatureRequired: readiness.guardianSignatureRequired === true,
    guardianRequest: readiness.guardianRequest ?? null,
  }
}

// ---------------------------------------------------------------------------
// Readiness refresh bus + profile-save sync (live-test bug fix, CMT-043)
// ---------------------------------------------------------------------------

/*
 * The live Get-ready subscription (useLiveGetReadyEvents) only re-fetches when
 * `liveState`/`tickets` change — neither of which changes when the participant
 * saves their profile in the wizard and returns to /get-ready. That left the
 * home showing a STALE pre-save readiness snapshot ("Details missing", "0 of 3
 * done") after the profile was completed. This tiny module-level bus lets a
 * profile save signal the subscription to re-read readiness immediately.
 */
const readinessRefreshListeners = new Set<() => void>()

/** Subscribe to readiness-refresh signals; returns an unsubscribe fn. */
export function subscribeReadinessRefresh(cb: () => void): () => void {
  readinessRefreshListeners.add(cb)
  return () => {
    readinessRefreshListeners.delete(cb)
  }
}

/** Signal every live Get-ready subscription to re-read server readiness. */
export function bumpReadinessRefresh(): void {
  for (const cb of [...readinessRefreshListeners]) cb()
}

/**
 * Called after the profile wizard persists (canon §5.6 Save). When the profile
 * is COMPLETE, records completion on the SERVER for every ticket the account
 * holds — the profile is account-wide (Cognito), so completing it satisfies the
 * get-ready "Your profile" step for each held ticket. It then signals the live
 * Get-ready subscription to re-read readiness, so returning to the home shows
 * the fresh server state rather than a pre-save snapshot.
 *
 * DEVIATION (flagged for boardroom ratification): Ruling C
 * (BOARDROOM-2026-08-09-133534) retired the client→server profile-complete call
 * on the intent that the server would DERIVE profile readiness. That derivation
 * was never implemented — my-api holds NO participant-profile data (it lives in
 * Cognito), so `ticket_holders.profile_complete` can only ever be set through
 * this endpoint. With nothing setting it, the get-ready profile step could
 * never flip to done (the live-test bug Duncan hit). Until a server-side
 * participant-profile store lands, the client must tell the server authority the
 * profile is complete. This records SERVER state (not a client-persisted
 * boolean), honouring Ruling C's intent while unblocking launch; a Ruling C
 * amendment should confirm this seam.
 */
// ---------------------------------------------------------------------------
// Profile-time eligibility rejections (BOARDROOM-2026-08-28-194055 item 2):
// a server 422 'ineligible' from the profile-complete call must SURFACE per
// ticket in the get-ready flow — the old catch swallowed it, so a rejected
// participant saw a silent "Details missing" with no reason. Module-level map
// (the readiness-refresh-bus pattern) recorded by onProfileSaved and consumed
// by the live Get-ready hook; a later SUCCESSFUL profile-complete for the
// ticket clears it. Non-422 failures keep the old tolerant behaviour (the
// readiness re-read reconciles) — only the eligibility verdict is retained.
// ---------------------------------------------------------------------------

export interface ProfileEligibilityRejection {
  /** Machine axis off the 422 body (gender|age|dob_required); null when the
   *  server sent none. 'dob_required' = the GATE 2 banded-seat missing-DOB
   *  refusal (BOARDROOM-2026-09-07-142718) — carried explicitly so the copy
   *  resolution below can fall to the server's own dob-required message. */
  reason: 'gender' | 'age' | 'dob_required' | null
  /** The server's plain-English copy — the honest fallback when no locale
   *  mapping applies. Never fabricated. */
  message: string | null
}

const profileRejectionsByTicket = new Map<string, ProfileEligibilityRejection>()

/** The recorded eligibility rejection for a held ticket, or null. */
export function profileEligibilityRejection(ticketId: string): ProfileEligibilityRejection | null {
  return profileRejectionsByTicket.get(ticketId) ?? null
}

/** Test hygiene — the map is module-level state. */
export function clearProfileEligibilityRejections(): void {
  profileRejectionsByTicket.clear()
}

// ---------------------------------------------------------------------------
// PROFILE AUTO-COMPLETE from the account (Duncan ruling BOARDROOM-2026-09-10-
// 184014 item 1, verbatim: "if the parameters have been met for that event,
// do not ask them again... It should just say complete. And an edit option").
// The onProfileSaved sweep below already gives SAVE-time per-person semantics;
// the hole it left: a holder minted AFTER the last save (a new self-assign /
// claim) sat unlatched until the NEXT save — Duncan's second ticket re-asked
// his complete profile from blank. This closes it: the get-ready load sweeps
// pending-profile holdings and latches them server-side when the SAVED account
// profile is complete. The caller gates on account completeness under the
// DEFAULT (strictest, ask-everything) config — event configs only ever REMOVE
// questions, so defaults-complete ⇒ met for every event's parameter set; a
// profile complete only under a reduced event set falls back to the wizard
// (conservative, never over-claims). Server enforcement is untouched: each
// latch is the same profile-complete POST (eligibility + GATE 2 run there);
// a 422 records into the SAME rejection store the card copy reads.
// ---------------------------------------------------------------------------

/** One attempt per ticket per session — a genuinely refused latch must not
 *  re-fire on every readiness refresh (the 422 is recorded; other errors
 *  leave the honest pending step + the wizard path). */
const autoLatchAttempted = new Set<string>()

/** Test hygiene. */
export function clearAutoLatchAttempts(): void {
  autoLatchAttempted.clear()
}

// ---------------------------------------------------------------------------
// GUARDIAN CHILD DETAILS (my-api V25): a guardian seat's profile-complete call
// answers 422 `child_details_required` until the parent has confirmed the
// child's name/DOB/gender on the server. That code MAPS TO THE CHILD-CONFIRM
// ROUTE: the ticket is recorded here and the Get-ready card treats it as a
// guardian seat (childConfirm row → /get-ready/child-confirm/{eventId}), even
// on a device with no local guardian record — never a dead end.
// ---------------------------------------------------------------------------

const childDetailsRequiredTickets = new Set<string>()

function noteChildDetailsRequired(ticketId: string, e: unknown): void {
  if (e instanceof MyApiError && e.status === 422 && e.code === CHILD_DETAILS_REQUIRED) {
    childDetailsRequiredTickets.add(ticketId)
  }
}

/** true ⇔ the server refused this ticket's profile-complete with
 *  child_details_required (this session) — the parent must confirm the child. */
export function childDetailsRequiredFor(ticketId: string): boolean {
  return childDetailsRequiredTickets.has(ticketId)
}

/** The route an API error maps to, when it names one: child_details_required
 *  → the ChildConfirm step for the event. null for every other error. */
export function routeForApiError(e: unknown, eventId: string, ticketId?: string): string | null {
  if (!(e instanceof MyApiError && e.code === CHILD_DETAILS_REQUIRED)) return null
  return ticketId
    ? `/get-ready/child-details/${encodeURIComponent(ticketId)}`
    : `/get-ready/child-confirm/${eventId}`
}

/** After the parent's child details are STORED (ChildConfirm success): clear
 *  this ticket's recorded refusals and its one-shot auto-latch guard, so the
 *  next Get-ready load re-attempts the profile-complete latch (it can now
 *  pass). */
export function childDetailsStored(ticketId: string): void {
  childDetailsRequiredTickets.delete(ticketId)
  autoLatchAttempted.delete(ticketId)
  profileRejectionsByTicket.delete(ticketId)
}

/** Test hygiene. */
export function clearChildDetailsRequired(): void {
  childDetailsRequiredTickets.clear()
}

/** The childConfirm step's done-state: the SERVER's childDetailsComplete when
 *  it sends one (V25), else the device-local record's confirmed flag. */
export function guardianChildConfirmed(server: boolean | null | undefined, localConfirmed: boolean): boolean {
  return typeof server === 'boolean' ? server : localConfirmed
}

/** Latch profile-complete on every given pending ticket from the account
 *  profile. Returns true when at least one latch SUCCEEDED (caller re-reads
 *  readiness). Best-effort per ticket; never throws. */
export async function autoCompleteProfilesFromAccount(
  ticketIds: string[],
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  const base = myApiBase()
  const session = loadSession()
  if (!base || !session) return false
  const targets = ticketIds.filter(
    (id) => !profileRejectionsByTicket.has(id) && !autoLatchAttempted.has(id),
  )
  if (targets.length === 0) return false
  let latched = false
  await Promise.all(
    targets.map(async (ticketId) => {
      autoLatchAttempted.add(ticketId)
      try {
        await markProfileComplete(session.idToken, base, ticketId, fetchImpl)
        latched = true
      } catch (e) {
        // Same 422 recording as onProfileSaved — the card then renders the
        // server's per-seat reject honestly instead of a silent no-op.
        if (e instanceof MyApiError && e.status === 422 && e.code === 'ineligible') {
          profileRejectionsByTicket.set(ticketId, {
            reason: e.reason === 'gender' || e.reason === 'age' || e.reason === 'dob_required' ? e.reason : null,
            message: e.serverMessage ?? null,
          })
        }
        noteChildDetailsRequired(ticketId, e)
        // Any other failure: honest pending step stands; the wizard remains.
      }
    }),
  )
  return latched
}

/** Locale-resolved plain copy for a recorded rejection: map the machine
 *  `reason` through the ticket's in-hand contract (i18n keys — Arabic falls
 *  back via the ar spread); no mappable contract → the server's own message;
 *  neither → the honest generic. Never blank, never fabricated numbers. */
export function profileBlockCopy(
  rejection: ProfileEligibilityRejection,
  eligibility: TicketEligibility | null | undefined,
  tr: TranslateFn = enT,
): string {
  // 'dob_required' has no i18n reject key (deliberate — the server's copy IS
  // the launch copy): it falls through to rejection.message below, same as an
  // unmappable age/gender axis.
  if (rejection.reason === 'gender' || rejection.reason === 'age') {
    const copy = eligibilityRejectCopy(rejection.reason, eligibility)
    if (copy) return tr(copy.key, copy.params)
  }
  return rejection.message ?? tr('eligibility.reject.generic')
}

// ---------------------------------------------------------------------------
// Truthful eligibility-mismatch overlay (Duncan rulings BOARDROOM-2026-08-29-
// 120259 #1–#3 + ADDENDUM 122136 + ADDENDUM 2 123126, CMT-2026-08-29-028).
//
// Duncan's walk ended with "Your profile — Details missing (2 of 3)" while the
// profile was COMPLETE and merely ineligible (gender 422). The rules below
// derive a distinct per-ticket state — "complete — not valid for this ticket"
// — for BOTH axes (gender + age), from data the FE already holds:
//  - the SERVER's readiness truth (profile step not done — the 422 kept
//    ticket_holders.profile_complete false),
//  - the participant's own SAVED profile (complete by wizardModel.allValid —
//    computed by the caller; this module stays free of wizard imports),
//  - the ticket's eligibility carry off the readiness wire (PR #83:
//    Readiness.eligibility {gender, ageMin, ageMax} + raceDay), and/or the
//    recorded profile-complete 422 (reason gender|age) from this session.
// NOTHING here writes completion state: the overlay is derived per render and
// clears on its own the moment the server flips the step done (e.g. after a
// support-side ticket swap re-runs profile-complete) — the ADDENDUM's
// zero-rework decoupling.
// ---------------------------------------------------------------------------

/** The saved SELF profile's mismatch-relevant attributes, loaded by the caller
 *  (screen layer owns profileStore access). `complete` = wizardModel.allValid
 *  over the SAVED draft — the honesty gate: without it the truthful copy
 *  "complete — not valid" would itself be a lie. */
export interface ProfileMismatchAttrs {
  gender: string
  birthdate: string
  complete: boolean
  /** Display name for the support pre-fill (their own saved name, never invented). */
  participantName: string
}

export interface ProfileEligibilityMismatch {
  /** Machine axis (gender|age); null only on a recorded 422 with no axis. */
  reason: 'gender' | 'age' | null
  /** Locale-resolved participant-voice copy ("This ticket is for a male
   *  participant." / the ticket's real age-band copy). */
  copy: string
  /** Rides to the support pre-fill. */
  participantName: string
}

/**
 * Decide whether a held ticket is in the mismatch state. Conservative and
 * honest on every input: server-done wins (no overlay), an incomplete saved
 * profile keeps the true "Details missing" (no overlay), an absent contract
 * axis never fires (eligibilityGate's allow-on-unknown posture; the recorded
 * 422 is the only other evidence and is itself server truth).
 */
export function detectProfileMismatch(input: {
  steps: GetReadyStep[]
  eligibility: TicketEligibility | null | undefined
  /** ISO race-day anchor off the wire — null skips the age axis (never a
   *  guessed date). Passed through as an INSTANT; its date parts are read in
   *  UTC inside ageOnDate (P1-2) so the FE verdict matches the server's UTC
   *  evaluation in every browser timezone — no local date-part reads exist
   *  anywhere on this path. */
  raceDay: string | null | undefined
  attrs: ProfileMismatchAttrs | null
  rejection: ProfileEligibilityRejection | null
  tr?: TranslateFn
}): ProfileEligibilityMismatch | null {
  const tr = input.tr ?? enT
  const profile = input.steps.find((s) => s.key === 'profile')
  // Server truth wins both ways: no profile row → nothing to overlay; server
  // says done → the ticket accepted this profile, no mismatch exists.
  if (!profile || profile.state === 'done') return null
  // Honesty gate: only a COMPLETE saved profile can be "complete — not valid".
  if (!input.attrs?.complete) return null
  const participantName = input.attrs.participantName
  // FE re-derivation from the carried contract — persists across sessions
  // (the 422 record below is module-level and dies on reload).
  const anchor = input.raceDay ? new Date(input.raceDay) : null
  const verdict = eligibilityGate(
    { gender: input.attrs.gender, birthdate: input.attrs.birthdate },
    input.eligibility ?? null,
    anchor && !Number.isNaN(anchor.getTime()) ? anchor : null,
  )
  if (!verdict.ok) {
    const copy = eligibilityMismatchCopy(verdict.kind, input.eligibility)
    return {
      reason: verdict.kind,
      copy: copy ? tr(copy.key, copy.params) : verdict.message,
      participantName,
    }
  }
  // Recorded profile-complete 422 for THIS ticket (session evidence — covers a
  // server-side restriction the wire carry cannot express, e.g. an axis the
  // carry omitted). The server's own message is the fallback copy.
  if (input.rejection) {
    return {
      // The mismatch overlay's machine axis stays gender|age (the support
      // pre-fill's MismatchReason domain); a dob_required rejection reads as
      // axis-less here — its copy (below) still carries the server's own
      // dob-required message.
      reason: input.rejection.reason === 'dob_required' ? null : input.rejection.reason,
      copy: profileBlockCopy(input.rejection, input.eligibility, tr),
      participantName,
    }
  }
  return null
}

/** Swap the profile row to the truthful blocked state: meta "Complete — not
 *  valid for this ticket" (i18n), outline Edit kept so the wizard stays
 *  reachable (Duncan explicitly did NOT block flows). Every other row —
 *  waiver, questionnaire, heat — is untouched: the mismatch is a per-ticket
 *  eligibility overlay, never a completion reset (ADDENDUM). */
export function applyProfileMismatch(steps: GetReadyStep[], tr: TranslateFn = enT): GetReadyStep[] {
  return steps.map((s) =>
    s.key === 'profile' && s.state !== 'done'
      ? { ...s, state: 'blocked' as const, meta: tr('getReady.step.profile.mismatch'), cta: 'Edit' as const }
      : s,
  )
}

export async function onProfileSaved(
  complete: boolean,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const base = myApiBase()
  const session = loadSession()
  if (complete && base && session) {
    try {
      const holdings = await fetchMyHoldings(session.idToken, base, fetchImpl)
      await Promise.all(
        holdings.map(async (h) => {
          try {
            await markProfileComplete(session.idToken, base, h.ticketId, fetchImpl)
            // A successful flip supersedes any earlier reject for this ticket.
            profileRejectionsByTicket.delete(h.ticketId)
            childDetailsRequiredTickets.delete(h.ticketId)
          } catch (e) {
            noteChildDetailsRequired(h.ticketId, e)
            // ELIGIBILITY 422 (BOARDROOM-2026-08-28-194055): RECORD it so the
            // get-ready card surfaces the server's plain reject — the step must
            // NOT read as done (server truth: profile_complete never flipped;
            // the refresh below re-reads it as 'now'). Every OTHER failure
            // stays non-fatal per ticket — the refresh re-reads server truth.
            if (e instanceof MyApiError && e.status === 422 && e.code === 'ineligible') {
              profileRejectionsByTicket.set(h.ticketId, {
                // 'dob_required' (GATE 2) is carried like 'age' — its copy
                // resolves via the server's own message in profileBlockCopy.
                reason: e.reason === 'gender' || e.reason === 'age' || e.reason === 'dob_required' ? e.reason : null,
                message: e.serverMessage ?? null,
              })
            }
          }
        }),
      )
    } catch {
      // Holdings fetch failed — skip the sync; the refresh still re-reads truth.
    }
  }
  bumpReadinessRefresh()
}

// ---------------------------------------------------------------------------
// Pre-save wizard gate contracts (BOARDROOM-2026-08-28-194055 items 3+4)
// ---------------------------------------------------------------------------

/** One held ticket's in-hand eligibility contract for the ProfileWizard
 *  pre-save gate. raceDay is the SERVER's age anchor (events.starts_at) —
 *  null skips the age axis, never a guessed date. eventId/eventName ride from
 *  the SAME holdings read (BOARDROOM-2026-08-29-120259 ruling 1: the wizard's
 *  fail-fast pre-check is TICKET-SCOPED to its `?event=` entry context —
 *  without the eventId it could not scope; account-wide entries fire NO
 *  pre-check). Honest thread of already-fetched data, nothing new invented. */
export interface HeldEligibilityContract {
  ticketId: string
  eventId: string
  eventName: string
  /** The holding's type label ("Individuals - Male") — rides so the wizard's
   *  advisory bar can NAME the seat its copy refers to (the 2026-09-09
   *  anonymous-banner incident: a foreign holding's mismatch rendered with no
   *  identity and was read as the entry ticket). Null when untitled. */
  ticketTypeName: string | null
  eligibility: TicketEligibility
  raceDay: Date | null
  /** The holding's wire-declared subject kind (child-holder slice), threaded
   *  from the SAME holdings read (review sweep R4): an EXPLICIT kind='self'
   *  is the wire saying "this seat is the caller's own", and consumers use it
   *  to ignore a stale device-local guardian-claim record (the PR#124 P1-2
   *  stale-record guard GetReady already applies — guardianRecordApplies in
   *  guardianClaim.ts). Optional: absent on an older my-api, in which case
   *  the guard cannot fire and behaviour is unchanged. */
  kind?: import('./myApi').HolderKind
}

/**
 * Gather the eligibility contracts IN HAND for every ticket the account holds
 * — the inputs of the wizard's instant pre-save gate. Sources, per holding:
 *  1. the my-api readiness carry (item 4 — covers INVITEES, whose payloads
 *     never carried a contract before), plus its raceDay anchor;
 *  2. the purchaser wire (`/consumer/me/orders` tickets, R1 fields) as the
 *     fallback for a purchaser's own holdings on an older my-api.
 * BEST-EFFORT by design: no session/base, a failed fetch, or a holding with no
 * contract in either source simply contributes nothing — the wizard gate skips
 * silently and the SERVER net catches it (never a blocked save on a guess).
 */
export async function fetchHeldEligibilityContracts(
  wireTickets: Ticket[] = [],
  fetchImpl: typeof fetch = fetch,
): Promise<HeldEligibilityContract[]> {
  const base = myApiBase()
  const session = loadSession()
  if (!base || !session) return []
  try {
    const holdings = await fetchMyHoldings(session.idToken, base, fetchImpl)
    if (holdings.length === 0) return []
    let readinessByTicket = new Map<string, Readiness>()
    try {
      const all = await fetchAllReadiness(session.idToken, base, fetchImpl)
      readinessByTicket = new Map(all.map((r) => [r.ticketId, r.readiness]))
    } catch {
      // Older my-api / transient failure — the purchaser-wire fallback below
      // still contributes; invitee contracts are simply not in hand.
    }
    const wireById = new Map(wireTickets.map((t) => [t.id, t]))
    const out: HeldEligibilityContract[] = []
    for (const h of holdings) {
      const r = readinessByTicket.get(h.ticketId)
      const eligibility = r?.eligibility ?? wireById.get(h.ticketId)?.eligibility ?? null
      if (!eligibility) continue // no contract in hand → the server net owns it
      const anchor = r?.raceDay ?? r?.eventStartsAt ?? null
      const raceDay = anchor ? new Date(anchor) : null
      out.push({
        ticketId: h.ticketId,
        eventId: h.eventId,
        eventName: h.eventName,
        ticketTypeName: (h.ticketTypeName ?? '').trim() || null,
        eligibility,
        raceDay: raceDay && !Number.isNaN(raceDay.getTime()) ? raceDay : null,
        // R4: the holding's own kind rides along (spread-omit keeps an older
        // my-api's absent field absent — never a fabricated 'self').
        ...(h.kind !== undefined ? { kind: h.kind } : {}),
      })
    }
    return out
  } catch {
    return [] // holdings unavailable → gate skips; server net still enforces
  }
}

// ---------------------------------------------------------------------------
// TEEN SELF-HOLDER — the waiver row relabelled (VITE_TEEN_SELF_HOLDER_ENABLED;
// design MY-DESIGN-JUNIOR-SELF-HOLDER-1020 §4.3.2; BOARDROOM-2026-09-07-103021
// one card, the waiver step naming the relationship). The teen cannot sign
// their own waiver (my-api 422 minor_self_signer), so the row never offers
// Sign: the card's guardian panel is the action.
//   ask     → 'now'  "Parent/guardian signature needed" · "Ask a parent or guardian to sign"
//   waiting → 'soon' "… · Waiting for {parent} to sign" (tag PARENT SIGNS)
//   signed  → 'done' "… · Signed by {parent}"
// ---------------------------------------------------------------------------

export function applyTeenGuardianWaiver(
  steps: GetReadyStep[],
  card: import('./teenGuardian').TeenGuardianCard,
  tr: TranslateFn = enT,
): GetReadyStep[] {
  const parent = card.parentName.trim()
  return steps.map((s) => {
    if (s.key !== 'waiver') return s
    const label = tr('getReady.step.waiver.labelTeen')
    if (card.state === 'signed') {
      return { key: 'waiver', label, meta: parent ? tr('getReady.step.waiver.teenSigned', { parent }) : tr('getReady.step.waiver.teenSignedNoName'), state: 'done' }
    }
    if (card.state === 'waiting') {
      return {
        key: 'waiver', label, state: 'soon', tag: tr('getReady.waiting.tag'),
        meta: parent ? tr('getReady.step.waiver.teenWaiting', { parent }) : tr('getReady.step.waiver.teenWaitingNoName'),
      }
    }
    return { key: 'waiver', label, state: 'now', meta: card.expired ? tr('getReady.step.waiver.teenExpired') : tr('getReady.step.waiver.teenAsk') }
  })
}
